import React, { useMemo } from 'react';
import moment from 'moment';

import { Languages } from 'constants/languages';
import { categories } from 'constants/tests';
import { InfoLabelsContainer, LabelsContainer, LabelHeading, LabelValue, SmallLabelValue } from '../styles';

export default function InfoBox({
  report,
  isFreecode,
  totalMaxScore,
  bestRunInput,
  timeUsed,
  completedAt,
  anonymiseReport,
}) {
  const clientInfo = useMemo(() => {
    if (!report?.ticket?.clientInfo?.length) return {};
    return {
      ip: Array.from(new Set(report.ticket.clientInfo.map((i) => i.ip))).join(', '),
      country: Array.from(new Set(report.ticket.clientInfo.map((i) => i.country))).join(', '),
    };
  }, [report?.ticket]);

  const convertMillisecondsToMinutesAndSeconds = (milliseconds) => {
    let seconds = milliseconds / 1000;
    if (seconds < 60) {
      const roundedSeconds = Math.round(seconds);
      return `${roundedSeconds} ${roundedSeconds !== 1 ? 'seconds' : 'second'}`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      let result = `${minutes} ${minutes !== 1 ? 'minutes' : 'minute'}`;
      if (remainingSeconds > 0) result += ` ${remainingSeconds} ${remainingSeconds !== 1 ? 'seconds' : 'second'}`;
      return result;
    }
  };

  return (
    <InfoLabelsContainer>
      <LabelsContainer anonymiseReport={anonymiseReport}>
        <LabelHeading>Name</LabelHeading>
        <LabelValue>{report.ticket.name ?? report.candidate.name}</LabelValue>
      </LabelsContainer>
      {report.candidate.email && (
        <LabelsContainer anonymiseReport={anonymiseReport}>
          <LabelHeading>Email</LabelHeading>
          <LabelValue breakWord>{report.candidate.email}</LabelValue>
        </LabelsContainer>
      )}
      <LabelsContainer>
        <LabelHeading>Test</LabelHeading>
        <LabelValue>{report.test.name}</LabelValue>
        {report.ticket.isCandidateChoosesEnabled === false && (
          <SmallLabelValue>Chosen by the candidate</SmallLabelValue>
        )}
      </LabelsContainer>
      <LabelsContainer>
        <LabelHeading>Test Type</LabelHeading>
        <LabelValue>
          {report.test.adaptiveTest
            ? 'Adaptive multi-choice'
            : report.test.category === categories.CODE.category
              ? 'Freecode test'
              : 'Multi-choice'}
        </LabelValue>
      </LabelsContainer>
      {clientInfo.country && !anonymiseReport && (
        <LabelsContainer>
          <LabelHeading>Location</LabelHeading>
          <LabelValue>{clientInfo.country}</LabelValue>
        </LabelsContainer>
      )}
      {clientInfo.ip && !anonymiseReport && (
        <LabelsContainer>
          <LabelHeading>IP</LabelHeading>
          <LabelValue>{clientInfo.ip}</LabelValue>
        </LabelsContainer>
      )}
      {!isFreecode && (
        <LabelsContainer>
          <LabelHeading>Correct answers</LabelHeading>
          <LabelValue>
            {report.ticket.result} / {totalMaxScore}
          </LabelValue>
        </LabelsContainer>
      )}
      {!isFreecode && (
        <LabelsContainer>
          <LabelHeading>Questions answered</LabelHeading>
          <LabelValue>
            {report.totalAnswered} / {totalMaxScore}
          </LabelValue>
        </LabelsContainer>
      )}
      {isFreecode && (
        <LabelsContainer>
          <LabelHeading>Test difficulty</LabelHeading>
          <LabelValue>{report.test.difficulty}</LabelValue>
        </LabelsContainer>
      )}
      {isFreecode && (
        <LabelsContainer>
          <LabelHeading>Code language</LabelHeading>
          <LabelValue>{bestRunInput.languageVersion || Languages[bestRunInput.language].SimpleName}</LabelValue>
        </LabelsContainer>
      )}
      <LabelsContainer>
        <LabelHeading>Time used</LabelHeading>
        <LabelValue>
          {report.ticket.isOvertime ? timeUsed : Math.min(timeUsed, report.ticket.timeLimit)} (min out of
          {` ${report.ticket.timeLimit}`} min) {report.ticket.isOvertime ? '(Overtime)' : ''}
        </LabelValue>
      </LabelsContainer>
      <LabelsContainer>
        <LabelHeading>Time sent</LabelHeading>
        <LabelValue>{moment(report.ticket.createdAt).format('YYYY-MM-DD HH:mm')}</LabelValue>
      </LabelsContainer>
      <LabelsContainer>
        <LabelHeading>Time completed</LabelHeading>
        <LabelValue>{completedAt ? moment(completedAt).format('YYYY-MM-DD HH:mm') : '--'}</LabelValue>
      </LabelsContainer>
      {(report.ticket.totalTimeAway > 0 || report.ticket.totalTimeAwayAfterOvertime > 0) && (
        <LabelsContainer>
          <LabelHeading>Total time away:</LabelHeading>
          <LabelValue>
            {convertMillisecondsToMinutesAndSeconds(report.ticket.totalTimeAway)}{' '}
            {report.ticket.totalTimeAwayAfterOvertime > 0
              ? `(${convertMillisecondsToMinutesAndSeconds(report.ticket.totalTimeAwayAfterOvertime)} on Overtime)`
              : ''}
          </LabelValue>
        </LabelsContainer>
      )}
      {report.ticket.customer && (
        <LabelsContainer>
          <LabelHeading>Sent by</LabelHeading>
          <LabelValue>{report.ticket.customer}</LabelValue>
        </LabelsContainer>
      )}
      {report.ticket?.reportReceivers?.length > 0 && (
        <LabelsContainer>
          <LabelHeading>Report Receiver</LabelHeading>
          <LabelValue>{report.ticket.reportReceivers.join(', ')}</LabelValue>
        </LabelsContainer>
      )}
    </InfoLabelsContainer>
  );
}
